import type { FormEvent } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, LoaderCircle, RefreshCw, ShieldCheck } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";

import {
  clearPendingOtpChallenge,
  readPendingOtpChallenge
} from "@/lib/pending-otp";
import {
  resendCandidateEmailOtp,
  verifyCandidateEmailOtp,
  type CandidateUser
} from "@/lib/api";

export function OtpVerificationPage() {
  const [challenge, setChallenge] = useState(() => readPendingOtpChallenge());
  const [code, setCode] = useState("");
  const [verifiedUser, setVerifiedUser] = useState<CandidateUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [nowValue, setNowValue] = useState(() => Date.now());

  useEffect(() => {
    const interval = window.setInterval(() => setNowValue(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, []);

  const resendCountdown = useMemo(() => {
    if (!challenge) {
      return 0;
    }

    return Math.max(0, Math.ceil((Date.parse(challenge.resendAvailableAt) - nowValue) / 1000));
  }, [challenge, nowValue]);

  const otpCountdown = useMemo(() => {
    if (!challenge) {
      return 0;
    }

    return Math.max(0, Math.ceil((Date.parse(challenge.expiresAt) - nowValue) / 1000));
  }, [challenge, nowValue]);

  if (!challenge && !verifiedUser) {
    return <Navigate replace to="/auth?role=candidate&mode=signup" />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!challenge) {
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setMessage(null);

    try {
      const response = await verifyCandidateEmailOtp({
        challengeId: challenge.challengeId,
        code
      });
      clearPendingOtpChallenge();
      setVerifiedUser(response.user);
      setMessage(response.message);
      setChallenge(null);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Unable to verify OTP.");
    } finally {
      setIsSubmitting(false);
    }
  }

  // Resend OTP to the user's email
  async function handleResend() {
    if (!challenge) {
      return;
    }
    setIsResending(true);
    setError(null);
    setMessage(null);
    try {
      const response = await resendCandidateEmailOtp({ challengeId: challenge.challengeId });
      setChallenge((prev) => prev ? {
        ...prev,
        resendAvailableAt: response.challenge.resendAvailableAt,
        developmentCode: response.challenge.developmentCode,
        expiresAt: response.challenge.expiresAt,
      } : prev);
      setMessage(response.message);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Unable to resend OTP.");
    } finally {
      setIsResending(false);
    }
  }

  useEffect(() => {
    // In local development the email isn’t sent automatically; trigger the resend flow on mount
    if (challenge) {
      void handleResend();
    }
  }, []);

  if (verifiedUser) {
    return (
      <section className="mx-auto max-w-[1100px] px-6 py-10 md:py-12" data-testid="otp-verified-page">
        <div className="surface mx-auto max-w-3xl p-8 md:p-10" data-testid="otp-verified-card">
          <p className="eyebrow">Email Verification</p>
          <h1 className="mt-3 text-4xl font-semibold tracking-tight text-ink" data-testid="otp-verified-heading">
            Email verification completed
          </h1>
          <p className="mt-4 text-base leading-8 text-slate" data-testid="otp-verified-message">
            {message ??
              "Your email address has been verified successfully. Your application is now under administrative review, and portal access will be enabled once approval is completed."}
          </p>

          <div className="mt-8 grid gap-4 md:grid-cols-2">
            <div className="rounded-[24px] border border-line/70 bg-cloud/70 p-5">
              <p className="text-sm font-semibold text-ink">Candidate</p>
              <p className="mt-2 text-sm leading-7 text-slate">{verifiedUser.fullName}</p>
            </div>
            <div className="rounded-[24px] border border-line/70 bg-cloud/70 p-5">
              <p className="text-sm font-semibold text-ink">Application status</p>
              <p className="mt-2 text-sm leading-7 text-slate">Under administrative review</p>
            </div>
          </div>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <Link
              className="btn-ghost justify-start px-0 py-0 text-sm text-slate hover:bg-transparent"
              to="/"
            >
              <ArrowLeft className="h-4 w-4" />
              Return to home
            </Link>
            <Link className="btn-primary" data-testid="otp-go-signin-link" to="/auth?role=candidate&mode=signin">
              Proceed to sign in
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-[1100px] px-6 py-10 md:py-12" data-testid="otp-page">
      <motion.div
        animate={{ opacity: 1, y: 0 }}
        className="surface mx-auto max-w-3xl p-7 md:p-8"
        data-testid="otp-card"
        initial={{ opacity: 0, y: 20 }}
        transition={{ delay: 0.06, duration: 0.45, ease: "easeOut" }}
      >
        <div className="border-b border-line/70 pb-6">
          <p className="eyebrow">Email Verification</p>
          <h2 className="mt-2 text-3xl font-semibold tracking-tight text-ink">
            Enter the verification code
          </h2>
          <p className="mt-3 max-w-xl text-sm leading-7 text-slate">OTP has been sent to your email address.</p>
        </div>

        <form className="mt-6 grid gap-5" data-testid="otp-form" onSubmit={handleSubmit}>
          <label className="block space-y-2.5">
            <span className="field-label">Verification code</span>
            <input
              autoComplete="one-time-code"
              className="input-field text-center text-2xl font-semibold tracking-[0.45em]"
              data-testid="otp-code-input"
              inputMode="numeric"
              maxLength={6}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
              placeholder="000000"
              required
            />

          </label>

          {error ? (
            <div
              className="rounded-[22px] border border-rose-200 bg-rose-50 px-5 py-4 text-sm font-medium text-rose-700"
              data-testid="otp-error-message"
            >
              {error}
            </div>
          ) : null}

          {message ? (
            <div
              className="rounded-[22px] border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm font-medium text-emerald-800"
              data-testid="otp-success-message"
            >
              {message}
            </div>
          ) : null}

          {otpCountdown > 0 && otpCountdown <= 60 ? (
            <div className="rounded-[22px] border border-amber-200 bg-amber-50 px-5 py-4 text-sm font-medium text-amber-900">
              Code expires in {otpCountdown}s — verify now or request a new code.
            </div>
          ) : otpCountdown === 0 ? (
            <div className="rounded-[22px] border border-rose-200 bg-rose-50 px-5 py-4 text-sm font-medium text-rose-700">
              This code has expired. Request a new one using the Resend button below.
            </div>
          ) : null}

          <div className="rounded-[24px] border border-line/70 bg-cloud/72 px-5 py-5">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-[16px] bg-white text-royal">
                <ShieldCheck className="h-4 w-4" />
              </div>
              <div>
                <p className="text-sm font-semibold text-ink">Next step</p>
                <p className="mt-2 text-sm leading-7 text-slate">
                  Once your email address has been verified, your application will move to the review stage. Portal access will be enabled after administrative approval.
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap gap-3">
              <Link
                className="btn-ghost justify-start px-0 py-0 text-sm text-slate hover:bg-transparent"
                data-testid="otp-back-signup-link"
                to="/auth?role=candidate&mode=signup"
              >
                <ArrowLeft className="h-4 w-4" />
                Return to registration
              </Link>
              <button
                className="btn-secondary px-4 py-2.5"
                data-testid="otp-resend-button"
                disabled={isResending || resendCountdown > 0}
                onClick={() => void handleResend()}
                type="button"
              >
                {isResending ? (
                  <LoaderCircle className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                {resendCountdown > 0 ? `Resend available in ${resendCountdown}s` : "Resend code"}
              </button>
            </div>

            <button
              className="btn-primary"
              data-testid="otp-submit"
              disabled={isSubmitting || otpCountdown === 0}
              type="submit"
            >
              {isSubmitting ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
              Verify email and continue
            </button>
          </div>
        </form>
      </motion.div>
    </section >
  );
}
